/**
 * Pitch Deck — /pitch
 * -------------------
 * Unlisted, passcode-gated, fixed 16:9 slide deck. Visual language
 * matches the rest of Levanta (navy + brand blue + italic accents +
 * parallax forest on the cover) so it doesn't look like a generic
 * decktape product.
 *
 * Sizing model
 *  Slides are designed inside a fixed virtual canvas of 1600×900 px
 *  (16:9). The runtime measures the actual stage and applies a CSS
 *  transform: scale(...) so content is identical on every viewport,
 *  just visually scaled. This is what keeps mobile, tablet and 4K
 *  layouts pixel-faithful — no responsive tier juggling per slide.
 *
 * Behaviour
 *  • Passcode: 'evolvex' (sessionStorage so refresh doesn't re-prompt).
 *  • Keyboard: ArrowRight / ArrowDown / Space / PageDown — next.
 *              ArrowLeft / ArrowUp / PageUp — prev.
 *              Home / End — first / last.
 *              Esc — exit to homepage.
 *  • Touch: swipe left = next, swipe right = prev (≥ 50px horizontal).
 *  • Deep link: /pitch#3 jumps to slide 3 (1-indexed).
 *  • Search engines: noindex, nofollow.
 *  • Print: a media query stacks every slide on its own A4-landscape
 *    page; "Save to PDF" triggers window.print() and the browser's
 *    native dialog handles the rest (one slide per PDF page).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Compass, Trophy, Users, Lock, ChevronLeft,
  ChevronRight, Download, X,
  Anchor, Hammer, Flame, BookOpen, Mic, Award, Newspaper,
  Target, Layers, Repeat, Play, Pause, Maximize, Minimize,
} from 'lucide-react';
import AltParallaxBackdrop from '../components/AltParallaxBackdrop';

/* ---------------------------------------------------------------- */
/*  Configuration                                                   */
/* ---------------------------------------------------------------- */

const PASSCODE = 'evolvex';
const SESSION_KEY = 'levanta_pitch_unlocked';

/* Virtual slide canvas — every slide is designed against this exact
   pixel grid. The runtime scales the canvas to fit the visible stage.
   Don't change without re-balancing every slide's typography. */
const CANVAS_W = 1600;
const CANVAS_H = 900;

const SLIDES = [
  /* 1. Cover — untouched, user liked this one */
  {
    type: 'cover',
    eyebrow: 'A pitch deck',
    title: 'Levanta',
    subtitle: 'A structured personal growth system.',
    footer: 'Lev Kazaryan — Founder',
    audio: '/pitch-audio/slide-01.mp3',
  },
  /* 2. The market has changed — knowledge vs application */
  {
    type: 'big-stat',
    eyebrow: 'The market has changed',
    statBig: '$50B',
    statBigCaption: 'personal growth industry, still built on delivering knowledge',
    body: 'For decades, personal growth meant delivering knowledge through books, courses, seminars and coaches. It worked when knowledge was scarce. Today knowledge is abundant, instant, and free. The real challenge isn\'t finding the right information anymore. It\'s applying it consistently enough to create lasting change. That\'s where the opportunity begins.',
    audio: '/pitch-audio/slide-02.mp3',
  },
  /* 3. Meet Yoni — the persona and the loop */
  {
    type: 'two-col',
    eyebrow: 'The story',
    title: 'Meet',
    title_em: 'Yoni.',
    body: "Yoni dreams of starting a business. He's read Rich Dad Poor Dad, watched every Shark Tank episode, listened to countless Y Combinator talks, and follows every successful entrepreneur online. He already knows far more than he needs to get started. But every time he's about to take the first step, the same thoughts show up. So he puts it off. Another week. Another month. Another year.",
    bullets: [
      { label: 'The thought', value: '"I\u2019m not ready yet."' },
      { label: 'The thought', value: '"Someone else is probably already doing it better."' },
      { label: 'The thought', value: '"What if I fail?"' },
    ],
    audio: '/pitch-audio/slide-03.mp3',
  },
  /* 4. Meet Neta — the persona and the loop */
  {
    type: 'two-col',
    eyebrow: 'The story',
    title: 'Now meet',
    title_em: 'Neta.',
    body: "Neta feels lonely. Whenever she meets someone she\u2019s interested in, she wants to walk over and start a conversation. But she never does. The same thoughts show up every time. So she stays quiet. And the moment passes.",
    bullets: [
      { label: 'The thought', value: '"What if he rejects me?"' },
      { label: 'The thought', value: '"What if I embarrass myself?"' },
      { label: 'The thought', value: '"Maybe I\u2019m just not good enough."' },
    ],
    audio: '/pitch-audio/slide-04.mp3',
  },
  /* 5. The real problem — the reveal that ties Yoni & Neta together */
  {
    type: 'big-stat',
    eyebrow: 'The real problem',
    statBig: '1',
    statBigCaption: 'problem shared by two very different people',
    body: "Two different people. Two completely different goals. But exactly the same problem. They don't lack information. They already know what they need to do. What holds them back are the beliefs and thought patterns that shape the decisions they make every single day.",
    audio: '/pitch-audio/slide-05.mp3',
  },
  /* 6. The solution — Levanta as a Life Skills Platform */
  {
    type: 'pillars',
    eyebrow: 'The solution',
    title: 'This is',
    title_em: 'Levanta.',
    body: "A Life Skills Platform. Instead of creating more content, we help people apply the best knowledge that already exists, through a structured process. Not a better product. An entirely new category. Business model: a $10 monthly subscription with full access to the platform.",
    items: [
      { Icon: BookOpen, name: 'Learning',    desc: 'The best of what already exists, curated and structured.' },
      { Icon: Repeat,   name: 'Practice',    desc: 'Daily reps that turn knowledge into behaviour.' },
      { Icon: Flame,    name: 'Consistency', desc: 'Streaks, tracking and a Tribe that keeps you moving.' },
    ],
    audio: '/pitch-audio/slide-06.mp3',
  },
  /* 7. Proof of demand — Duolingo benchmark + $120M ARR opportunity */
  {
    type: 'three-stats',
    eyebrow: 'Proof of demand',
    title: 'The opportunity, in numbers.',
    items: [
      {
        big: '138M',
        caption: 'Duolingo monthly active users',
        sub: "Millions of people come back to an app every single day to build just one skill: learning a language.",
      },
      {
        big: '12.5M',
        caption: 'Duolingo paying subscribers',
        sub: 'Proof that people are willing to pay every month to keep building a skill inside an app.',
      },
      {
        big: '$120M',
        caption: 'ARR at 1M paying subscribers',
        sub: 'Just 1 million subscribers at $10 per month, and life skills demand is at least as significant.',
      },
    ],
    audio: '/pitch-audio/slide-07.mp3',
  },
  /* 8. Today & Team — MVP status + sole founder */
  {
    type: 'two-col',
    eyebrow: 'Today & team',
    title: 'Live MVP,',
    title_em: 'sole founder.',
    body: "Levanta has a live MVP on iOS and Android. Current focus: reaching product-market fit and validating strong user retention. I\u2019m Lev Kazaryan, the sole founder. Before Levanta I held several management roles, became a certified Master NLP practitioner, and published a book on personal development. This isn\u2019t just a market I decided to enter, it\u2019s a field I\u2019ve studied and lived personally. To date, Levanta has been built entirely by me.",
    bullets: [
      { label: 'Product', value: 'Live MVP on iOS and Android. Focus: product-market fit and retention.' },
      { label: 'Founder', value: 'Lev Kazaryan. Certified Master NLP practitioner. Published author on personal development.' },
      { label: 'Built by', value: 'A single founder, to date. Ready to scale with the right partners.' },
    ],
    audio: '/pitch-audio/slide-08.mp3',
  },
  /* 9. The future — closing vision + thank you */
  {
    type: 'closing',
    eyebrow: 'The future',
    title: "The world\u2019s",
    title_em: 'leading platform.',
    body: "We believe the future of personal growth won\u2019t be built on more books, more courses, or more content. It will be built on platforms that genuinely help people develop skills and create lasting change. Our vision is to build the world\u2019s leading platform for developing life skills. Thank you.",
    contacts: [
      { label: 'Email', value: 'business@levanta.life' },
      { label: 'Web',   value: 'levanta.life' },
    ],
    audio: '/pitch-audio/slide-09.mp3',
  },
];

/* ---------------------------------------------------------------- */
/*  Slide renderers — designed against a fixed 1600×900 canvas.     */
/*  No `md:`/`sm:` Tailwind variants here: the wrapper handles      */
/*  responsive scaling via CSS transform.                           */
/* ---------------------------------------------------------------- */

const Eyebrow = ({ children }) => (
  <p className="text-[14px] font-semibold uppercase tracking-[0.22em] text-blue-600">
    {children}
  </p>
);

const SlideShell = ({ children, dark = false, parallax = false }) => (
  <div
    className={`absolute inset-0 flex flex-col ${
      dark ? 'bg-[#0b1f3a] text-white' : 'bg-white text-[#0b1f3a]'
    }`}
  >
    {parallax && <AltParallaxBackdrop />}
    {/* Legibility scrim — soft left-to-right white wash that lets the
        parallax forest stay visible on the right while giving text on
        the left a clean canvas. Only applied to parallax slides. */}
    {parallax && (
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            'linear-gradient(100deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.78) 28%, rgba(255,255,255,0.45) 52%, rgba(255,255,255,0) 78%)',
        }}
      />
    )}
    <div className="relative z-10 flex-1 flex flex-col justify-center px-24 py-16">
      {children}
    </div>
  </div>
);

const SlideRenderer = ({ slide }) => {
  switch (slide.type) {
    /* COVER */
    case 'cover':
      return (
        <SlideShell parallax>
          <div className="flex-1 flex flex-col justify-center max-w-4xl">
            <Eyebrow>{slide.eyebrow}</Eyebrow>
            <h1 className="mt-8 text-[9rem] font-black leading-[0.95] tracking-tight text-[#0b1f3a] alt-hero-glow">
              {slide.title}
            </h1>
            <p className="mt-6 text-[2rem] font-semibold italic text-blue-700 leading-tight alt-hero-glow">
              {slide.subtitle}
            </p>
          </div>
          <div className="text-base text-[#0b1f3a] font-semibold alt-hero-glow">
            {slide.footer}
          </div>
        </SlideShell>
      );

    /* BIG STAT */
    case 'big-stat':
      return (
        <SlideShell dark>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <div className="mt-10 flex items-baseline gap-10 flex-wrap">
            <span className="text-[14rem] font-black leading-none text-blue-400">
              {slide.statBig}
            </span>
            <span className="text-[2rem] font-medium text-white/85 max-w-xl leading-tight">
              {slide.statBigCaption}
            </span>
          </div>
          <p className="mt-12 text-xl text-white/65 leading-relaxed max-w-3xl">
            {slide.body}
          </p>
        </SlideShell>
      );

    /* TWO COLUMN — left text + right bullet matrix */
    case 'two-col':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight max-w-4xl">
            {slide.title}{' '}
            <em className="italic font-light text-blue-600">{slide.title_em}</em>
          </h2>
          <div className="mt-12 grid grid-cols-2 gap-12 max-w-5xl">
            <p className="text-xl text-[#0b1f3a]/75 leading-relaxed">
              {slide.body}
            </p>
            <div className="space-y-5">
              {slide.bullets.map((b, i) => (
                <div key={i} className="border-l-2 border-blue-500 pl-5 py-1">
                  <p className="text-sm font-semibold uppercase tracking-wider text-blue-600 mb-1">
                    {b.label}
                  </p>
                  <p className="text-lg text-[#0b1f3a]/80 leading-snug">
                    {b.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </SlideShell>
      );

    /* MEGA HEADLINE — full focus */
    case 'mega-headline':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-8 text-8xl font-black leading-[1.0] tracking-tight max-w-5xl">
            {slide.title}{' '}
            <em className="italic font-light text-blue-600">{slide.title_em}</em>
          </h2>
          <p className="mt-10 text-2xl text-[#0b1f3a]/70 max-w-3xl leading-relaxed">
            {slide.body}
          </p>
        </SlideShell>
      );

    /* FOUR GRID — Four Worlds + optional daily rhythm footer */
    case 'four-grid':
      return (
        <SlideShell dark>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight text-white">
            {slide.title}
          </h2>
          <p className="mt-5 text-xl text-white/65 max-w-3xl leading-relaxed">
            {slide.subtitle}
          </p>
          <div className="mt-10 grid grid-cols-4 gap-5 max-w-6xl">
            {slide.items.map(({ Icon, name, desc }) => (
              <div
                key={name}
                className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-6 backdrop-blur-sm"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-blue-300" strokeWidth={2.2} />
                </div>
                <h3 className="text-2xl font-black tracking-tight text-white mb-1">
                  {name}
                </h3>
                <p className="text-sm text-white/65 leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
          {slide.rhythm && (
            <div className="mt-10 flex items-center gap-5 text-white/80 flex-wrap">
              <Repeat className="w-5 h-5 text-blue-400" strokeWidth={2.2} />
              <p className="text-[11px] uppercase tracking-[0.22em] text-blue-400 font-semibold">
                Daily rhythm
              </p>
              {slide.rhythm.map((step, i) => (
                <React.Fragment key={step}>
                  <span className="text-xl font-semibold tracking-tight">{step}</span>
                  {i < slide.rhythm.length - 1 && (
                    <span className="text-xl text-blue-400/60">→</span>
                  )}
                </React.Fragment>
              ))}
            </div>
          )}
        </SlideShell>
      );

    /* PILLARS — 3 vertical pillar cards with icons (used for Solution) */
    case 'pillars':
      return (
        <SlideShell dark>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-8xl font-black leading-[1.02] tracking-tight text-white max-w-5xl">
            {slide.title}{' '}
            <em className="italic font-light text-blue-400">{slide.title_em}</em>
          </h2>
          <p className="mt-6 text-xl text-white/65 max-w-3xl leading-relaxed">
            {slide.body}
          </p>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-5xl">
            {slide.items.map(({ Icon, name, desc }) => (
              <div
                key={name}
                className="rounded-2xl bg-white/5 ring-1 ring-white/10 p-8 backdrop-blur-sm"
              >
                <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center mb-6">
                  <Icon className="w-7 h-7 text-blue-300" strokeWidth={2.2} />
                </div>
                <h3 className="text-3xl font-black tracking-tight text-white mb-3">
                  {name}
                </h3>
                <p className="text-base text-white/70 leading-relaxed">
                  {desc}
                </p>
              </div>
            ))}
          </div>
        </SlideShell>
      );

    /* TIERS — stacked model rows (used for Business Model) */
    case 'tiers':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight max-w-4xl">
            {slide.title}{' '}
            <em className="italic font-light text-blue-600">{slide.title_em}</em>
          </h2>
          <p className="mt-6 text-lg text-[#0b1f3a]/70 max-w-3xl leading-relaxed">
            {slide.body}
          </p>
          <div className="mt-10 space-y-0 max-w-5xl">
            {slide.items.map((t, i) => (
              <div
                key={t.name}
                className="grid grid-cols-[60px_220px_1fr] items-baseline gap-6 py-5 border-t border-[#0b1f3a]/10 first:border-t-0"
              >
                <span className="text-sm font-mono tracking-wider text-blue-600 font-semibold">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <p className="text-3xl font-black tracking-tight text-[#0b1f3a]">
                  {t.name}
                </p>
                <p className="text-lg text-[#0b1f3a]/75 leading-snug">
                  {t.desc}
                </p>
              </div>
            ))}
          </div>
        </SlideShell>
      );

    /* FLOW — 4 step LPRT */
    case 'flow':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight max-w-4xl">
            {slide.title}{' '}
            <em className="italic font-light text-blue-600">{slide.title_em}</em>
          </h2>
          <div className="mt-14 grid grid-cols-4 gap-6 max-w-6xl">
            {slide.steps.map((s, i) => (
              <div key={s.name} className="relative">
                <div className="text-[110px] font-black text-blue-100 leading-none -mb-2">
                  0{i + 1}
                </div>
                <h3 className="text-2xl font-black tracking-tight text-[#0b1f3a]">
                  {s.name}
                </h3>
                <p className="mt-2 text-base text-[#0b1f3a]/70 leading-snug">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>
        </SlideShell>
      );

    /* PRODUCT */
    case 'product':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight max-w-4xl">
            {slide.title}{' '}
            <em className="italic font-light text-blue-600">{slide.title_em}</em>
          </h2>
          <div className="mt-12 grid grid-cols-2 gap-12 items-start max-w-5xl">
            <p className="text-xl text-[#0b1f3a]/75 leading-relaxed">
              {slide.body}
            </p>
            <ul className="space-y-3">
              {slide.bullets.map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-lg text-[#0b1f3a]/85"
                >
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-blue-600 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </SlideShell>
      );

    /* COMPARISON */
    case 'comparison':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-6xl font-black leading-[1.05] tracking-tight max-w-4xl">
            {slide.title}{' '}
            <em className="italic font-light text-blue-600">{slide.title_em}</em>
          </h2>
          <div className="mt-10 max-w-4xl">
            {slide.rows.map((r) => {
              const isUs = r[0] === 'Levanta';
              return (
                <div
                  key={r[0]}
                  className={`grid grid-cols-[220px_1fr] gap-6 py-4 border-b border-[#0b1f3a]/10 last:border-b-0 ${
                    isUs ? 'bg-blue-50/60 -mx-4 px-4 rounded-xl border-b-0' : ''
                  }`}
                >
                  <p
                    className={`text-lg font-bold ${
                      isUs ? 'text-blue-600' : 'text-[#0b1f3a]'
                    }`}
                  >
                    {r[0]}
                  </p>
                  <p
                    className={`text-lg ${
                      isUs ? 'font-semibold text-[#0b1f3a]' : 'text-[#0b1f3a]/65'
                    }`}
                  >
                    {r[1]}
                  </p>
                </div>
              );
            })}
          </div>
        </SlideShell>
      );

    /* THREE STATS */
    case 'three-stats':
      return (
        <SlideShell dark>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight text-white">
            {slide.title}
          </h2>
          <div className="mt-14 grid grid-cols-3 gap-10 max-w-6xl">
            {slide.items.map((it, i) => (
              <div key={i}>
                <div className="text-8xl font-black leading-none text-blue-400">
                  {it.big}
                </div>
                <p className="mt-5 text-xl text-white font-semibold leading-snug">
                  {it.caption}
                </p>
                <p className="mt-2 text-base text-white/55 leading-snug">
                  {it.sub}
                </p>
              </div>
            ))}
          </div>
        </SlideShell>
      );

    /* MILESTONES */
    case 'milestones':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight">
            {slide.title}
          </h2>
          <div className="mt-12 grid grid-cols-3 gap-6 max-w-5xl">
            {slide.items.map(({ Icon, label, value }) => (
              <div
                key={label}
                className="rounded-2xl bg-[#fafbfd] ring-1 ring-black/5 p-6"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-500/15 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-blue-600" strokeWidth={2.4} />
                </div>
                <p className="text-[12px] uppercase tracking-[0.18em] text-[#0b1f3a]/55 font-semibold mb-1">
                  {label}
                </p>
                <p className="text-lg font-bold text-[#0b1f3a]">
                  {value}
                </p>
              </div>
            ))}
          </div>
        </SlideShell>
      );

    /* TIMELINE */
    case 'timeline':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <h2 className="mt-6 text-7xl font-black leading-[1.05] tracking-tight">
            {slide.title}
          </h2>
          <div className="mt-14 max-w-5xl">
            {slide.items.map((it) => (
              <div
                key={it.period}
                className="grid grid-cols-[200px_1fr] gap-6 py-5 border-t border-[#0b1f3a]/10 first:border-t-0"
              >
                <p className="text-lg font-bold text-blue-600">
                  {it.period}
                </p>
                <p className="text-xl text-[#0b1f3a]/85 leading-snug">
                  {it.focus}
                </p>
              </div>
            ))}
          </div>
        </SlideShell>
      );

    /* FOUNDER */
    case 'founder':
      return (
        <SlideShell>
          <Eyebrow>{slide.eyebrow}</Eyebrow>
          <div className="mt-8 grid grid-cols-[300px_1fr] gap-12 items-start max-w-5xl">
            <div className="rounded-3xl aspect-[4/5] bg-gradient-to-br from-blue-500/10 to-blue-100 ring-1 ring-blue-200/60 flex items-center justify-center">
              <Compass className="w-24 h-24 text-blue-600/60" strokeWidth={1.6} />
            </div>
            <div>
              <h2 className="text-7xl font-black leading-[1.0] tracking-tight">
                {slide.name}
              </h2>
              <p className="mt-3 text-xl italic text-blue-600 font-light">
                {slide.role}
              </p>
              <p className="mt-7 text-lg text-[#0b1f3a]/75 leading-relaxed">
                {slide.body}
              </p>
              <p className="mt-7 text-base text-[#0b1f3a] font-semibold leading-relaxed border-l-2 border-blue-500 pl-4">
                {slide.looking}
              </p>
            </div>
          </div>
        </SlideShell>
      );

    /* CLOSING */
    case 'closing':
      return (
        <SlideShell parallax>
          <div className="flex-1 flex flex-col justify-center max-w-4xl">
            <Eyebrow>{slide.eyebrow}</Eyebrow>
            <h2 className="mt-8 text-8xl font-black leading-[0.98] tracking-tight alt-hero-glow">
              {slide.title}{' '}
              <em className="italic font-light text-blue-600">{slide.title_em}</em>
            </h2>
            <p className="mt-8 text-2xl text-[#0b1f3a] font-medium leading-relaxed max-w-3xl alt-hero-glow">
              {slide.body}
            </p>
            <div className="mt-10 inline-flex flex-wrap gap-x-10 gap-y-4 px-7 py-5 bg-white/80 backdrop-blur-sm rounded-2xl ring-1 ring-black/5 shadow-md self-start">
              {slide.contacts.map((c) => (
                <div key={c.label}>
                  <p className="text-[12px] uppercase tracking-[0.18em] text-blue-600 font-semibold mb-1">
                    {c.label}
                  </p>
                  <p className="text-xl font-bold text-[#0b1f3a]">
                    {c.value}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="text-sm text-[#0b1f3a]/70 font-medium tracking-wider alt-hero-glow">
            {`Levanta — ${new Date().getFullYear()}`}
          </div>
        </SlideShell>
      );

    default:
      return null;
  }
};

/* ---------------------------------------------------------------- */
/*  Scaling slide stage — measures container and scales canvas.     */
/* ---------------------------------------------------------------- */

const ScaledStage = ({ children, testId, maxWidth = 1700, fillViewport = false }) => {
  const stageRef = useRef(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.clientWidth;
      if (w > 0) {
        /* Over-scale by a hair so the canvas always slightly exceeds
           the stage on the right/bottom — kills sub-pixel rounding
           gaps that otherwise leak the stage's bg as a hairline. The
           stage has overflow:hidden so the extra is clipped. */
        setScale(w / CANVAS_W + 1 / CANVAS_W);
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Normal mode: width is the smaller of (a) the design max, (b) the
     parent's available width, and (c) what would let a 16:9 box still
     fit under the viewport's chrome budget.
     Fullscreen mode: fill the entire viewport with a 16:9 box (letterbox
     lets the ROOT backdrop show through and match the slide colour). */
  const widthStyle = fillViewport
    ? 'min(100vw, calc(100dvh * 16 / 9))'
    : `min(${maxWidth}px, 100%, calc((100vh - 9rem) * 16 / 9))`;
  return (
    <div
      ref={stageRef}
      data-testid={testId}
      style={{
        width: widthStyle,
        aspectRatio: '16 / 9',
      }}
      className={`lv-pitch-stage relative overflow-hidden bg-[#0b1f3a] ${
        fillViewport
          ? ''
          : 'rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] ring-1 ring-white/10'
      }`}
    >
      <div
        className="absolute top-0 left-0"
        style={{
          width: `${CANVAS_W}px`,
          height: `${CANVAS_H}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        {children}
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- */
/*  Passcode gate                                                   */
/* ---------------------------------------------------------------- */

const PasscodeGate = ({ onUnlock }) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (value.trim().toLowerCase() === PASSCODE) {
      sessionStorage.setItem(SESSION_KEY, '1');
      onUnlock();
    } else {
      setError(true);
      setValue('');
      setTimeout(() => setError(false), 800);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0b1f3a] flex items-center justify-center px-6">
      <AltParallaxBackdrop />
      <form
        onSubmit={handleSubmit}
        className="relative z-10 w-full max-w-md rounded-3xl bg-white/95 backdrop-blur shadow-2xl ring-1 ring-black/5 px-8 py-10"
        data-testid="pitch-gate-form"
      >
        <div className="w-12 h-12 rounded-2xl bg-blue-500/15 flex items-center justify-center mb-5">
          <Lock className="w-5 h-5 text-blue-600" strokeWidth={2.2} />
        </div>
        <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-2">
          Unlisted
        </p>
        <h1 className="text-3xl font-black text-[#0b1f3a] tracking-tight">
          Levanta — pitch deck
        </h1>
        <p className="mt-3 text-sm text-[#0b1f3a]/65 leading-relaxed">
          This page is shared by invitation. Enter the passcode you received.
        </p>
        <input
          ref={inputRef}
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          aria-label="Passcode"
          data-testid="pitch-gate-input"
          className={`mt-7 w-full px-4 py-3 rounded-xl bg-white ring-1 transition-colors text-[#0b1f3a] tracking-wider font-mono text-lg focus:outline-none ${
            error
              ? 'ring-red-400 focus:ring-red-500'
              : 'ring-black/10 focus:ring-blue-500'
          }`}
        />
        {error && (
          <p
            className="mt-3 text-xs text-red-600 font-medium"
            data-testid="pitch-gate-error"
          >
            That passcode didn&rsquo;t match.
          </p>
        )}
        <button
          type="submit"
          data-testid="pitch-gate-submit"
          className="mt-6 w-full px-6 py-3 rounded-xl bg-[#0b1f3a] text-white font-semibold hover:shadow-2xl hover:-translate-y-0.5 transition-all duration-200"
        >
          Open the deck →
        </button>
      </form>
    </div>
  );
};

/* ---------------------------------------------------------------- */
/*  Main page                                                       */
/* ---------------------------------------------------------------- */

const AUDIO_PREF_KEY = 'levanta_pitch_audio_paused';

const Pitch = () => {
  const location = useLocation();
  /* /pitchnexa is a passcode-shared duplicate of /pitch with distinct
     narration on some cover/intro slides. List the 1-indexed slide
     numbers that have a nexa-specific track under /pitch-audio/nexa/. */
  const isNexa = location.pathname.startsWith('/pitchnexa');
  const NEXA_AUDIO_OVERRIDES = [1, 2, 3, 4, 5, 6, 7, 8, 9];
  const slides = useMemo(() => {
    if (!isNexa) return SLIDES;
    return SLIDES.map((s, i) => {
      const slideNo = i + 1;
      if (!NEXA_AUDIO_OVERRIDES.includes(slideNo)) return s;
      const padded = String(slideNo).padStart(2, '0');
      return { ...s, audio: `/pitch-audio/nexa/slide-${padded}.mp3` };
    });
  }, [isNexa]);

  const [unlocked, setUnlocked] = useState(
    () => sessionStorage.getItem(SESSION_KEY) === '1',
  );
  const [current, setCurrent] = useState(() => {
    const hash = parseInt(window.location.hash.replace('#', ''), 10);
    if (Number.isFinite(hash) && hash >= 1 && hash <= SLIDES.length) {
      return hash - 1;
    }
    return 0;
  });
  const [paused, setPaused] = useState(
    /* Default: paused on a fresh visit (no session pref stored). The
       cover slide (slide 1) has its own narration — we don't want it
       to autoplay before the user has clicked anything. Only after
       the user presses Play once do we remember their choice. */
    () => sessionStorage.getItem(AUDIO_PREF_KEY) !== '0',
  );
  const [isFullscreen, setIsFullscreen] = useState(false);
  /* CSS-based fake fullscreen fallback — iOS Safari does not support
     Element.requestFullscreen() on non-video elements, so we stretch the
     deck to fill the viewport with a fixed-position class instead. */
  const [useFakeFs, setUseFakeFs] = useState(false);
  const total = SLIDES.length;
  const audioRef = useRef(null);
  const rootRef = useRef(null);

  /* Document title + crawler hint */
  useEffect(() => {
    document.title = 'Levanta — Pitch Deck';
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    meta.id = 'lv-pitch-noindex';
    document.head.appendChild(meta);
    return () => {
      const node = document.getElementById('lv-pitch-noindex');
      if (node) node.remove();
    };
  }, []);

  /* Sync hash with current slide */
  useEffect(() => {
    if (!unlocked) return;
    const target = `#${current + 1}`;
    if (window.location.hash !== target) {
      window.history.replaceState(null, '', `/pitch${target}`);
    }
  }, [current, unlocked]);

  /* Per-slide audio narration. When the SLIDE changes, the old track
     stops, the new one loads from the start and auto-plays (the
     passcode click counts as the user gesture). The play/pause button
     pauses/resumes WITHOUT resetting position — so the listener can
     stop, take a breath, and continue from where they left off. */
  useEffect(() => {
    if (!unlocked) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;
    const src = slides[current]?.audio || '';
    if (!src) {
      el.pause();
      el.removeAttribute('src');
      el.load();
      return undefined;
    }
    el.src = src;
    el.currentTime = 0;
    if (!paused) {
      const tryPlay = () => el.play().catch(() => {});
      tryPlay();
      /* If autoplay was blocked (reload with cached session), retry on
         the very next user gesture anywhere on the page. */
      const onAnyGesture = () => {
        if (el.paused && !paused) tryPlay();
      };
      window.addEventListener('pointerdown', onAnyGesture, { once: true });
      window.addEventListener('keydown', onAnyGesture, { once: true });
      return () => {
        el.pause();
        window.removeEventListener('pointerdown', onAnyGesture);
        window.removeEventListener('keydown', onAnyGesture);
      };
    }
    /* slide changed but user has audio paused — load the source so
       a later "play" click will start from this slide's track. */
    el.pause();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, unlocked, slides]);

  /* Pause / resume without changing position. Triggered by the
     play-pause button. Slide audio still auto-advances to the next
     slide via the `ended` handler set in the effect above. */
  useEffect(() => {
    if (!unlocked) return;
    const el = audioRef.current;
    if (!el) return;
    if (paused) {
      el.pause();
    } else if (el.src) {
      /* Call .play() unconditionally. If the file hasn't been fetched
         yet (preload="none", readyState=0), .play() will trigger the
         load itself — waiting for readyState here would silently drop
         the very first Play click on slide 1. */
      el.play().catch(() => {});
    }
  }, [paused, unlocked]);

  /* Single persistent `ended` listener — advances exactly one slide when
     the current audio finishes. Attached once per unlock so we can never
     accidentally fire twice for the same `ended` event. */
  useEffect(() => {
    if (!unlocked) return undefined;
    const el = audioRef.current;
    if (!el) return undefined;
    const onEnded = () => {
      setCurrent((c) => Math.min(c + 1, SLIDES.length - 1));
    };
    el.addEventListener('ended', onEnded);
    return () => el.removeEventListener('ended', onEnded);
  }, [unlocked]);

  const togglePaused = useCallback(() => {
    /* Decide whether this click will START playback (Play or "advance
       from a no-audio slide"). Pausing should NEVER change fullscreen. */
    const noAudio = !slides[current]?.audio;
    const willStartPlaying = noAudio || paused;

    if (willStartPlaying) {
      /* Enter fullscreen on Play so the deck fills the viewport for
         both mobile and desktop viewers. On iOS Safari the native
         Fullscreen API is not supported on non-video elements, so we
         fall back to a fixed-position CSS overlay. */
      const alreadyFs =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        useFakeFs;
      if (!alreadyFs) {
        const el = rootRef.current;
        const req =
          el && (
            el.requestFullscreen ||
            el.webkitRequestFullscreen ||
            el.msRequestFullscreen
          );
        if (req) {
          try {
            const p = req.call(el);
            if (p && typeof p.catch === 'function') {
              p.catch(() => setUseFakeFs(true));
            }
          } catch (_) {
            setUseFakeFs(true);
          }
        } else {
          /* API not supported at all — go straight to fake FS. */
          setUseFakeFs(true);
        }
        /* Force landscape on mobile so the 16:9 deck fills the screen
           regardless of how the phone is held. Silently ignored on
           browsers that don't support the Screen Orientation API. */
        try {
          if (window.screen?.orientation?.lock) {
            const p = window.screen.orientation.lock('landscape');
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        } catch (_) { /* ignore unsupported */ }
      }
    }

    /* If the user clicks the play/pause button on a slide that has no
       audio, interpret it as "start the deck" — jump to the next
       slide and ensure audio is unpaused. */
    if (noAudio) {
      setPaused(false);
      sessionStorage.setItem(AUDIO_PREF_KEY, '0');
      setCurrent((c) => Math.min(c + 1, SLIDES.length - 1));
      return;
    }
    setPaused((p) => {
      const next = !p;
      sessionStorage.setItem(AUDIO_PREF_KEY, next ? '1' : '0');
      return next;
    });
  }, [current, paused, useFakeFs, slides]);

  /* Toggle fullscreen without touching audio state. Handles both native
     and fake-fullscreen modes. */
  const toggleFullscreen = useCallback(() => {
    const doc = document;
    const nativeFs = doc.fullscreenElement || doc.webkitFullscreenElement;

    const releaseOrientation = () => {
      try {
        if (window.screen?.orientation?.unlock) {
          window.screen.orientation.unlock();
        }
      } catch (_) { /* ignore */ }
    };
    const requestLandscape = () => {
      try {
        if (window.screen?.orientation?.lock) {
          const p = window.screen.orientation.lock('landscape');
          if (p && typeof p.catch === 'function') p.catch(() => {});
        }
      } catch (_) { /* ignore */ }
    };

    if (nativeFs) {
      const exit =
        doc.exitFullscreen || doc.webkitExitFullscreen || doc.msExitFullscreen;
      if (exit) exit.call(doc);
      releaseOrientation();
      return;
    }
    if (useFakeFs) {
      setUseFakeFs(false);
      releaseOrientation();
      return;
    }
    const el = rootRef.current;
    if (!el) return;
    const req =
      el.requestFullscreen ||
      el.webkitRequestFullscreen ||
      el.msRequestFullscreen;
    if (req) {
      try {
        const p = req.call(el);
        if (p && typeof p.catch === 'function') {
          p.catch(() => setUseFakeFs(true));
        }
      } catch (_) {
        setUseFakeFs(true);
      }
    } else {
      setUseFakeFs(true);
    }
    requestLandscape();
  }, [useFakeFs]);

  /* Keep isFullscreen state in sync with either the browser's native
     fullscreen (covers ESC key, user-initiated exit, etc.) or our
     fake-fullscreen fallback used on iOS Safari. */
  useEffect(() => {
    const sync = () => {
      const fs = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(fs || useFakeFs);
      /* If native FS was exited by any means (ESC key, browser UI, gesture),
         release any orientation lock so the phone can rotate freely again. */
      if (!fs && !useFakeFs) {
        try {
          if (window.screen?.orientation?.unlock) {
            window.screen.orientation.unlock();
          }
        } catch (_) { /* ignore */ }
      }
    };
    sync();
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [useFakeFs]);

  /* Lock body scroll while fake fullscreen is active so a stray swipe
     doesn't scroll the underlying page. */
  useEffect(() => {
    if (!useFakeFs) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [useFakeFs]);

  const next = useCallback(() => {
    setCurrent((c) => Math.min(c + 1, total - 1));
  }, [total]);
  const prev = useCallback(() => {
    setCurrent((c) => Math.max(c - 1, 0));
  }, []);
  const goto = useCallback(
    (idx) => {
      setCurrent(Math.max(0, Math.min(idx, total - 1)));
    },
    [total],
  );

  /* Keyboard navigation */
  useEffect(() => {
    if (!unlocked) return undefined;
    const onKey = (e) => {
      // Don't hijack typing inside inputs.
      if (e.target?.matches?.('input, textarea, [contenteditable="true"]')) {
        return;
      }
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
        case ' ':
          e.preventDefault();
          next();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          e.preventDefault();
          prev();
          break;
        case 'Home':
          e.preventDefault();
          goto(0);
          break;
        case 'End':
          e.preventDefault();
          goto(total - 1);
          break;
        case 'Escape':
          window.location.href = '/';
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [unlocked, next, prev, goto, total]);

  /* Touch swipe */
  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) next();
      else prev();
    }
    touchStartX.current = null;
  };

  const handlePrint = () => {
    window.print();
  };

  /* Pre-rendered slide list for the (hidden) print stack — kept outside
     the screen tree so each slide gets a clean print page. */
  const printPages = useMemo(
    () =>
      SLIDES.map((s, i) => (
        <div className="lv-pitch-print-page" key={`p-${i}`}>
          <SlideRenderer slide={s} />
        </div>
      )),
    [],
  );

  if (!unlocked) {
    return <PasscodeGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div
      ref={rootRef}
      className={`bg-black lv-pitch-root ${
        isFullscreen
          ? 'lv-pitch-fs w-screen h-screen relative overflow-hidden'
          : 'min-h-screen flex flex-col items-center justify-center px-3 sm:px-4 md:px-10 py-4 md:py-8'
      } ${useFakeFs ? 'lv-fake-fs' : ''}`}
      data-testid="pitch-deck"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar — inline flow when windowed, absolute overlay when fullscreen */}
      <header
        className={`lv-pitch-chrome ${
          isFullscreen
            ? 'absolute top-0 left-0 right-0 z-20 px-3 md:px-5 py-3 md:py-4 flex items-center justify-between text-white/85 text-xs md:text-sm pointer-events-none'
            : 'w-full max-w-[1700px] flex items-center justify-between text-white/70 text-xs md:text-sm mb-3 md:mb-4 px-1 sm:px-2'
        }`}
      >
        <span
          className={`font-mono tracking-wider ${
            isFullscreen ? 'pointer-events-auto bg-black/40 backdrop-blur-sm px-2.5 py-1.5 rounded-full' : ''
          }`}
        >
          Levanta · pitch
        </span>
        <div
          className={`flex items-center gap-3 ${
            isFullscreen ? 'pointer-events-auto' : ''
          }`}
        >
          {(() => {
            /* On slides without audio, the button always reads "Play"
               because clicking it starts the deck (advances + plays).
               On audio slides, it reflects the real paused state. */
            const noAudio = !slides[current]?.audio;
            const showAsPaused = noAudio || paused;
            return (
              <button
                type="button"
                onClick={togglePaused}
                aria-label={showAsPaused ? 'Play narration' : 'Pause narration'}
                data-testid="pitch-mute-btn"
                title={showAsPaused ? 'Play' : 'Pause narration'}
                className={`inline-flex items-center gap-2 px-3 py-2 rounded-full transition-colors text-white ${
                  showAsPaused
                    ? 'bg-white/10 hover:bg-white/20'
                    : 'bg-blue-500/80 hover:bg-blue-500'
                }`}
              >
                {showAsPaused ? (
                  <Play className="w-3.5 h-3.5" strokeWidth={2.4} />
                ) : (
                  <Pause className="w-3.5 h-3.5" strokeWidth={2.4} />
                )}
                <span className="hidden sm:inline">
                  {showAsPaused ? 'Play' : 'Pause'}
                </span>
              </button>
            );
          })()}
          <button
            type="button"
            onClick={toggleFullscreen}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            data-testid="pitch-fs-btn"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
          >
            {isFullscreen ? (
              <Minimize className="w-3.5 h-3.5" strokeWidth={2.4} />
            ) : (
              <Maximize className="w-3.5 h-3.5" strokeWidth={2.4} />
            )}
          </button>
          <button
            type="button"
            onClick={handlePrint}
            data-testid="pitch-print-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
          >
            <span className="text-[10px] font-mono font-semibold tracking-[0.16em] uppercase">
              PDF
            </span>
            <Download className="w-3.5 h-3.5" strokeWidth={2.4} />
          </button>
          <a
            href="/"
            aria-label="Exit deck"
            data-testid="pitch-close-btn"
            className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white"
          >
            <X className="w-4 h-4" />
          </a>
        </div>
      </header>

      {/* Hidden audio element — source is swapped per slide. */}
      <audio ref={audioRef} preload="none" data-testid="pitch-audio" />

      {/* Slide stage — fixed 16:9, content scales to fit.
          In fullscreen the stage grows to fill the entire viewport
          (letterbox filled with slide bg colour via matched navy). */}
      {isFullscreen ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <ScaledStage testId="pitch-stage" fillViewport>
            <SlideRenderer slide={slides[current]} />
          </ScaledStage>
        </div>
      ) : (
        <ScaledStage testId="pitch-stage">
          <SlideRenderer slide={slides[current]} />
        </ScaledStage>
      )}

      {/* Bottom controls */}
      <footer
        className={`lv-pitch-chrome ${
          isFullscreen
            ? 'absolute bottom-0 left-0 right-0 z-20 px-3 md:px-5 py-3 md:py-4 flex items-center justify-between gap-2 text-white/85 text-xs md:text-sm pointer-events-none'
            : 'w-full max-w-[1700px] mt-3 md:mt-4 flex items-center justify-between gap-2 text-white/70 text-xs md:text-sm px-1 sm:px-2'
        }`}
      >
        <button
          type="button"
          onClick={prev}
          disabled={current === 0}
          aria-label="Previous slide"
          data-testid="pitch-prev-btn"
          className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white disabled:opacity-30 disabled:hover:bg-white/10 shrink-0 ${
            isFullscreen ? 'pointer-events-auto backdrop-blur-sm bg-black/40 hover:bg-black/60' : ''
          }`}
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Prev</span>
        </button>
        <div
          className={`flex items-center gap-1.5 md:gap-2.5 overflow-x-auto no-scrollbar ${
            isFullscreen ? 'pointer-events-auto bg-black/40 backdrop-blur-sm px-3 py-2 rounded-full' : ''
          }`}
        >
          {SLIDES.map((_, i) => (
            <button
              type="button"
              key={i}
              onClick={() => goto(i)}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`pitch-dot-${i + 1}`}
              className={`transition-all rounded-full shrink-0 ${
                i === current
                  ? 'w-6 h-1.5 bg-white'
                  : 'w-1.5 h-1.5 bg-white/30 hover:bg-white/55'
              }`}
            />
          ))}
        </div>
        <button
          type="button"
          onClick={next}
          disabled={current === total - 1}
          aria-label="Next slide"
          data-testid="pitch-next-btn"
          className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors text-white disabled:opacity-30 disabled:hover:bg-white/10 shrink-0 ${
            isFullscreen ? 'pointer-events-auto backdrop-blur-sm bg-black/40 hover:bg-black/60' : ''
          }`}
        >
          <span className="hidden sm:inline">Next</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </footer>

      {/* Slide counter — hidden in fullscreen (footer overlay is enough) */}
      {!isFullscreen && (
        <p className="lv-pitch-chrome mt-2 md:mt-3 text-[11px] font-mono tracking-wider text-white/40">
          {String(current + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </p>
      )}

      {/* Print-mode: every slide rendered at full 1600×900 canvas;
          the print CSS scales the page to A4 landscape so each page
          is one slide. The screen-mode stack is hidden via CSS. */}
      <div className="lv-pitch-print-stack" aria-hidden="true">
        {printPages}
      </div>
    </div>
  );
};

export default Pitch;
